import { FC, MouseEvent, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import styled, { css } from 'styled-components';

import FancySwitch from '@/components/FancySwitch';
import ShowHideButton from '@/components/ShowHideButton';
import Text from '@/components/Text';
import { MediaResource } from '@/types/resources';
import { CoverImagesImageStatus } from '@/utils/enums';
import { RootState } from '@/utils/store';

import ArticleButtons from './ArticleButtons';
import GeneralComment from './GeneralComment';
import Image from './Image';
import ImagesPlaceholder from './ImagesPlaceholder';
import LightBox from './LightBox';
import SelectionActions from './SelectionActions';

const Box = styled.div`
  border-radius: 4px;
  background: ${({ theme }) => theme.colors.white};
  box-shadow: 0px 0px 2px 0px rgba(0, 0, 0, 0.08);
  margin-bottom: 24px;
`;
const Header = styled.div`
  padding: 24px;
  border-bottom: 1px solid ${({ theme }) => theme.colors.lightGray};
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const Heading = styled(Text)`
  margin-bottom: 8px;
  span {
    color: ${({ theme }) => theme.colors.gray};
  }
`;

const StyledSelectableGroup = styled.div<{
  $isPreviewMode?: boolean;
  $isFullScreenMode: boolean;
}>`
  user-select: none;
  position: relative;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
  padding: 32px 24px;
  ${({ $isFullScreenMode }) =>
    $isFullScreenMode &&
    css`
      grid-template-columns: repeat(3, 1fr);
    `}
  ${({ $isPreviewMode }) =>
    $isPreviewMode &&
    css`
      grid-template-columns: repeat(4, 1fr);
      gap: 12px;
    `}
    ${({ $isFullScreenMode, $isPreviewMode }) =>
    $isFullScreenMode &&
    $isPreviewMode &&
    css`
      grid-template-columns: repeat(6, 1fr);
      gap: 12px;
    `}
`;

const ActionsHolder = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`;

const SelectionBox = styled.div<{
  $left?: number;
  $top?: number;
  $width?: number;
  $height?: number;
}>`
  position: absolute;
  background-color: rgba(0, 0, 0, 0.16);
  pointer-events: none;
  border: dashed 1px black;
  top: ${({ $top }) => $top}px;
  left: ${({ $left }) => $left}px;
  width: ${({ $width }) => $width}px;
  height: ${({ $height }) => $height}px;
`;

interface Rectangle {
  left: number;
  top: number;
  right: number;
  bottom: number;
}

interface SelectionRect {
  x1: number;
  y1: number;
  x2: number;
  y2: number;
}

interface Props {
  comment?: string;
  status: CoverImagesImageStatus;
  sentImagesCount?: number;
  images?: MediaResource<
    {},
    {
      status: CoverImagesImageStatus;
      comment: string;
    }
  >[];
}

const ImagesBox: FC<Props> = ({ images, status, comment, sentImagesCount }) => {
  const [showContent, setShowContent] = useState(true);
  const [selectedImages, setSelectedImages] = useState<number[]>([]);
  const [selectableImages, setSelectableImages] = useState<number[]>([]);
  const [lightboxIndex, setLightboxIndex] = useState(-1);
  const [isPreviewMode, setIsPreviewMode] = useState(true);

  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [dragCurrent, setDragCurrent] = useState({ x: 0, y: 0 });
  const [selectionRect, setSelectionRect] = useState<{
    x1: number;
    y1: number;
    x2: number;
    y2: number;
  }>();

  const galleryRef = useRef<HTMLDivElement>(null);

  const fullScreenModeEnabled = useSelector(
    (rootState: RootState) => rootState.layout.fullScreenModeEnabled,
  );

  const renderTitleBasedOnStatus = () => {
    switch (status) {
      case CoverImagesImageStatus.PENDING:
        return 'Pending Covers';
      case CoverImagesImageStatus.FIXING:
        return 'Covers to Fix';
      case CoverImagesImageStatus.APPROVED:
        return 'Approved Covers';
      case CoverImagesImageStatus.RESOLVED:
        return 'Resolved Covers';
      case CoverImagesImageStatus.SENT:
        return 'Sent to article';
    }
  };

  const renderSubtitleBasedOnStatus = () => {
    switch (status) {
      case CoverImagesImageStatus.PENDING:
        return 'Approve selected covers or move them to fix';
      case CoverImagesImageStatus.FIXING:
        return 'Fix selected covers to send them for the additional approval';
      case CoverImagesImageStatus.APPROVED:
        return 'Approved covers that are ready to distribute';
      case CoverImagesImageStatus.RESOLVED:
        return 'Already fixed covers';
      case CoverImagesImageStatus.SENT:
        return 'Covers that were submitted for a Bored Panda article';
    }
  };

  const handleMouseDown = (e: MouseEvent<HTMLDivElement>) => {
    e.preventDefault();
    const rect = galleryRef.current?.getBoundingClientRect();
    if (!rect) return;
    setIsDragging(true);
    setDragStart({ x: e.clientX - rect.left, y: e.clientY - rect.top });
    setDragCurrent({ x: e.clientX - rect.left, y: e.clientY - rect.top });
  };

  const handleMouseMove = (e: MouseEvent<HTMLDivElement>) => {
    if (!isDragging) return;
    const rect = galleryRef.current?.getBoundingClientRect();
    if (!rect) return;

    setDragCurrent({ x: e.clientX - rect.left, y: e.clientY - rect.top });

    checkImagesWhileSelecting();
  };

  const handleMouseUp = () => {
    setIsDragging(false);
    updateSelection();
    setSelectableImages([]);
    setSelectionRect(undefined);
  };

  useEffect(() => {
    if (!isDragging) return;

    const newRect = {
      x1: Math.min(dragStart.x, dragCurrent.x),
      y1: Math.min(dragStart.y, dragCurrent.y),
      x2: Math.max(dragStart.x, dragCurrent.x),
      y2: Math.max(dragStart.y, dragCurrent.y),
    };

    setSelectionRect(newRect);
  }, [dragCurrent, isDragging, dragStart]);

  const checkImagesWhileSelecting = () => {
    const newSelectedImages = images
      ?.filter((image) => {
        const imgNode = document.getElementById(`image-${image.id}`);
        const imgRect = imgNode?.getBoundingClientRect();
        const galleryRect = galleryRef.current?.getBoundingClientRect();

        if (!imgRect || !galleryRect) return null;
        const relativeRect = {
          left: imgRect.left - galleryRect.left,
          top: imgRect.top - galleryRect.top,
          right: imgRect.right - galleryRect.left,
          bottom: imgRect.bottom - galleryRect.top,
        };

        return intersects(relativeRect, selectionRect);
      })
      .map((image) => image.id);
    if (!newSelectedImages?.length) return;
    setSelectableImages(newSelectedImages);
  };

  const updateSelection = () => {
    const newSelectedImages = images
      ?.filter((image) => {
        const imgNode = document.getElementById(`image-${image.id}`);
        const imgRect = imgNode?.getBoundingClientRect();
        const galleryRect = galleryRef.current?.getBoundingClientRect();

        if (!imgRect || !galleryRect) return null;
        const relativeRect = {
          left: imgRect.left - galleryRect.left,
          top: imgRect.top - galleryRect.top,
          right: imgRect.right - galleryRect.left,
          bottom: imgRect.bottom - galleryRect.top,
        };

        return intersects(relativeRect, selectionRect);
      })
      .map((image) => image.id);
    if (!newSelectedImages?.length) return;
    setSelectedImages((prev) => [
      ...prev,
      ...newSelectedImages.filter((image) => !prev.includes(image)),
    ]);
  };

  const intersects = (
    imgRect: Rectangle,
    selectionRect: SelectionRect | undefined,
  ): boolean => {
    if (!selectionRect) return false;

    const selRectConverted: Rectangle = {
      left: selectionRect.x1,
      top: selectionRect.y1,
      right: selectionRect.x2,
      bottom: selectionRect.y2,
    };

    return !(
      selRectConverted.right < imgRect.left ||
      selRectConverted.left > imgRect.right ||
      selRectConverted.bottom < imgRect.top ||
      selRectConverted.top > imgRect.bottom
    );
  };

  return (
    <>
      <Box>
        {!!images?.length && (
          <LightBox
            lightboxIndex={lightboxIndex}
            setLightboxIndex={setLightboxIndex}
            images={images}
          />
        )}
        <Header>
          <div>
            <Heading $type="button">
              {renderTitleBasedOnStatus()}{' '}
              <span>
                · For{' '}
                {status === CoverImagesImageStatus.FIXING ||
                status === CoverImagesImageStatus.RESOLVED
                  ? 'Specialists'
                  : 'Editors'}
              </span>
            </Heading>
            <Text $type="smallHeadline" $color="gray">
              {renderSubtitleBasedOnStatus()}
            </Text>
          </div>
          <ActionsHolder>
            <FancySwitch
              isActive={isPreviewMode}
              onClick={() => setIsPreviewMode((prev) => !prev)}
              text="Preview mode:"
            />
            <ShowHideButton
              showContent={showContent}
              onClick={() => setShowContent((prev) => !prev)}
            />
          </ActionsHolder>
        </Header>
        {showContent && (
          <>
            {!!images?.length ? (
              <>
                {status !== CoverImagesImageStatus.SENT && (
                  <SelectionActions
                    selectedImages={selectedImages}
                    imagesCount={images.length}
                    setSelectedImages={setSelectedImages}
                    onSelectAll={() =>
                      setSelectedImages(images.map((image) => image.id))
                    }
                    status={status}
                    comment={comment}
                  />
                )}
                {status === CoverImagesImageStatus.FIXING && !!comment && (
                  <GeneralComment comment={comment} />
                )}
                <StyledSelectableGroup
                  draggable="true"
                  onDragStart={handleMouseDown}
                  onMouseMove={handleMouseMove}
                  onMouseUp={handleMouseUp}
                  $isPreviewMode={isPreviewMode}
                  $isFullScreenMode={fullScreenModeEnabled}
                  ref={galleryRef}
                >
                  {images.map((image, index) => (
                    <Image
                      status={status}
                      isPreviewMode={isPreviewMode}
                      selectedImages={selectedImages}
                      selectableImages={selectableImages}
                      setSelectedImages={setSelectedImages}
                      onLightboxClick={() => setLightboxIndex(index)}
                      image={image}
                      key={index}
                      sentImagesCount={sentImagesCount}
                    />
                  ))}
                  {isDragging && selectionRect && (
                    <SelectionBox
                      $left={selectionRect.x1}
                      $top={selectionRect.y1}
                      $width={selectionRect.x2 - selectionRect.x1}
                      $height={selectionRect.y2 - selectionRect.y1}
                    />
                  )}
                </StyledSelectableGroup>
              </>
            ) : (
              <ImagesPlaceholder />
            )}
          </>
        )}
      </Box>
      {showContent && (
        <ArticleButtons
          setSelectedImages={setSelectedImages}
          selectedImages={selectedImages}
          status={status}
          images={images}
          sentImagesCount={sentImagesCount}
        />
      )}
    </>
  );
};

export default ImagesBox;
